import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledSubheader, StyledPermissionSettings } from '../styles';
import PermissionSettings from 'components/Sharing/PermissionSettings';
import { IconWithTooltip } from 'pages/Common/components/LinkWithIconTooltip/IconWithTooltip';

export const SharingSettingsBlock = ({
  id,
  sharingSettings,
  owner,
  onChange,
  clearError,
}) => {
  const { t } = useTranslation();

  const fisrtOnChangeTriggered = useRef(false);

  const onChangeSharing = useCallback(
    ({ allTeamMembersPermission, viewRoles, viewTeamMembers }) => {
      // <PermissionSettings /> component calls onChange in useEffect so we need to prevent unnecessary onChange calls,
      // we skip first call cuz it has always wrong empty values, and we skip if nothing changed
      if (!fisrtOnChangeTriggered.current) {
        fisrtOnChangeTriggered.current = true;
        return;
      }
      const isDirty =
        sharingSettings.all_team_members !== allTeamMembersPermission ||
        sharingSettings.roles?.length !== viewRoles.length ||
        sharingSettings.team_members?.length !== viewTeamMembers.length ||
        sharingSettings.roles?.some(
          ({ id }) => !viewRoles.some(({ value }) => value === id)
        ) ||
        sharingSettings.team_members?.some(
          ({ id }) => !viewTeamMembers.some(({ value }) => value === id)
        );
      if (!isDirty) {
        return;
      }
      onChange(id, 'sharing_settings', {
        all_team_members: allTeamMembersPermission,
        roles: viewRoles.map(({ value, label }) => ({
          id: value,
          name: label,
        })),
        team_members: viewTeamMembers.map(({ value, label }) => ({
          id: value,
          display_name: label,
        })),
      });
      clearError('sharing_settings');
    },
    [onChange, id, sharingSettings, clearError]
  );

  const permissionLabels = useMemo(
    () => ({
      all: {
        view: t('VIEW/USE'),
      },
      specific: {
        view: t('View/Use'),
      },
    }),
    [t]
  );

  const sharingSettingsValue = useRef({
    all_team_members: sharingSettings.all_team_members,
    roles: {
      view: sharingSettings.roles.map(({ id, name }) => ({
        id,
        display_name: name,
      })),
    },
    team_members: { view: sharingSettings.team_members },
  });

  return (
    <>
      <StyledSubheader weight="bold" type="subheader">
        {t('Sharing Settings')}{' '}
        <IconWithTooltip
          label={t(
            'Matching any of the criteria below will grant the team member access to use this custom action.'
          )}
        />
      </StyledSubheader>
      <StyledPermissionSettings>
        <PermissionSettings
          labels={permissionLabels}
          onChange={onChangeSharing}
          existing={sharingSettingsValue.current}
          owner={owner}
          inline
          disableFocus
          menuInline
        />
      </StyledPermissionSettings>
    </>
  );
};
